import {
    IdentityProviderType,
    SsoOpenIdConnectionResult,
    SsoOpenIdConnectionResultUserNotification,
} from '@mews-commander/data-access';
import { LocalizedDateFromUtc } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Divider, Grid, Stack, StackItem, Tag, Typography } from '@optimus-web/core';

export interface SsoDetailsPanelConnectionConfigProps {
    data: SsoOpenIdConnectionResult;
}

export function SsoDetailsPanelConnectionConfig({ data }: SsoDetailsPanelConnectionConfigProps) {
    return (
        <Stack vertical>
            <Divider />
            <ConnectionDetail
                title={TextKey.SsoSetupIdentityProvider}
                value={getIdentityProviderLabel(data.identityProviderType)}
            />
            <ConnectionDetail title={TextKey.ClientId} value={data.clientId} />
            <ConnectionDetail title={TextKey.SsoSetupIdpDomain} value={data.idpDomain} />
            <ConnectionDetail
                title={TextKey.SsoSetupClientSecret}
                value={<TokenAndExpirationDate expirationDate={data.expirationDate} />}
            />
            <ConnectionDetail
                title={TextKey.EmailAddressPlural}
                value={<SsoNotificationEmails userNotifications={data.userNotifications} />}
            />
        </Stack>
    );
}

const ConnectionDetail = ({ title, value }: { title: Mews.TextKey; value?: React.ReactNode }) => {
    if (!value) {
        return null;
    }
    return (
        <>
            <StackItem padding="200" data-test-id={title}>
                <Grid templateColumns="0.5fr 1fr">
                    <Typography textStyle="bodyMedium" variant="secondary" as="h3">
                        <LocalizedText textKey={title} />
                    </Typography>
                    <Typography textStyle="bodyMedium" as="div">
                        {value}
                    </Typography>
                </Grid>
            </StackItem>
            <Divider />
        </>
    );
};

const identityLabelMap = {
    [IdentityProviderType.MicrosoftAzure]: <LocalizedText textKey={TextKey.MicrosoftAzure} />,
    [IdentityProviderType.GoogleWorkspace]: <LocalizedText textKey={TextKey.GoogleAuthenticator} />,
};

function getIdentityProviderLabel(identityProviderType: IdentityProviderType | undefined | null) {
    if (!identityProviderType) {
        return <LocalizedText textKey={TextKey.None} />;
    }

    return identityLabelMap[identityProviderType];
}

const hiddenToken = UnicodeEntity.Asterisk.repeat(10);

const TokenAndExpirationDate = ({ expirationDate }: { expirationDate: string | undefined | null }) => (
    <>
        <Typography textStyle="bodyMedium" as="div">
            {hiddenToken}
        </Typography>
        {expirationDate && (
            <Typography textStyle="bodyMedium" as="div">
                <LocalizedText
                    textKey={TextKey.ExpiresOn}
                    substitutes={{ date: <LocalizedDateFromUtc utcString={expirationDate} /> }}
                />
            </Typography>
        )}
    </>
);

function SsoNotificationEmails({
    userNotifications,
}: {
    userNotifications: SsoOpenIdConnectionResultUserNotification[] | null | undefined;
}) {
    if (!userNotifications || userNotifications.length === 0) {
        return (
            <Typography textStyle="bodyMedium" variant="primary" as="div">
                {UnicodeEntity.Dash}
            </Typography>
        );
    }

    return (
        <Stack spacing="100" wrap>
            {userNotifications.map((data) => (
                <Tag variant="basicSubtle" key={data.userId}>
                    {data.userEmailAddress}
                </Tag>
            ))}
        </Stack>
    );
}
